import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import { Badge } from '@/components/ui/badge'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import { Copy, Plus, Trash2, Eye, EyeOff } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'

const tokenFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be less than 100 characters'),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
  systemName: z.string().max(100).optional(),
  autoApprove: z.boolean().optional(),
  expiresInDays: z.number().min(1, 'Must be at least 1 day').max(730, 'Cannot exceed 2 years'),
})

type TokenFormValues = z.infer<typeof tokenFormSchema>

interface ApiToken {
  id: string
  name: string
  description?: string
  system_name?: string | null
  auto_approve?: boolean
  expires_at: string
  created_at: string
  last_used_at?: string
  is_active: boolean
}


export function ApiTokenManager() {
  const { user } = useAuth()
  const [tokens, setTokens] = useState<ApiToken[]>([])
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [generatedToken, setGeneratedToken] = useState<string | null>(null)
  const [isTokenVisible, setIsTokenVisible] = useState(false)
  const [loading, setLoading] = useState(false)

  const form = useForm<TokenFormValues>({
    resolver: zodResolver(tokenFormSchema),
    defaultValues: {
      name: '',
      description: '',
      systemName: '',
      autoApprove: false,
      expiresInDays: 365,
    },
  })


  const fetchTokens = async () => {
    try {
      const { data, error } = await supabase
        .from('api_tokens')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setTokens(data || [])
    } catch (error) {
      console.error('Error fetching tokens:', error)
      toast.error('Failed to fetch API tokens')
    }
  }

  useEffect(() => {
    fetchTokens()
  }, [])

  const onSubmit = async (values: TokenFormValues) => {
    if (!user) return

    setLoading(true)
    try {
      const { data, error } = await supabase.functions.invoke('generate-api-token', {
        body: values
      })

      if (error) throw error

      setGeneratedToken(data.token)
      toast.success('API token generated successfully')
      await fetchTokens()
      form.reset()
    } catch (error: any) {
      console.error('Error generating token:', error)
      toast.error(error.message || 'Failed to generate API token')
    } finally {
      setLoading(false)
    }
  }

  const revokeToken = async (tokenId: string) => {
    try {
      const { error } = await supabase
        .from('api_tokens')
        .update({ is_active: false })
        .eq('id', tokenId)

      if (error) throw error

      toast.success('Token revoked successfully')
      await fetchTokens()
    } catch (error) {
      console.error('Error revoking token:', error)
      toast.error('Failed to revoke token')
    }
  }

  const copyToken = () => {
    if (generatedToken) {
      navigator.clipboard.writeText(generatedToken)
      toast.success('Token copied to clipboard')
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date()
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>API Token Management</CardTitle>
        <CardDescription>
          Generate long-lived API tokens for accessing the get-purchase-data endpoint from external systems.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium">API Tokens</h3>
            <p className="text-sm text-muted-foreground">
              Manage API tokens for external access to purchase data
            </p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Generate Token
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Generate API Token</DialogTitle>
                <DialogDescription>
                  Create a new API token for external access to purchase data.
                </DialogDescription>
              </DialogHeader>
              
              {generatedToken ? (
                <div className="space-y-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm font-medium mb-2">Your API Token</p>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 p-2 bg-background rounded border text-sm font-mono break-all">
                        {isTokenVisible ? generatedToken : '••••••••••••••••••••••••••••••••'}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsTokenVisible(!isTokenVisible)}
                      >
                        {isTokenVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button variant="outline" size="sm" onClick={copyToken}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      ⚠️ This token will only be shown once. Make sure to copy and store it securely.
                    </p>
                  </div>
                  <Button 
                    onClick={() => {
                      setGeneratedToken(null)
                      setIsTokenVisible(false)
                      setIsCreateDialogOpen(false)
                    }}
                    className="w-full"
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Token Name</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g., External Analytics System" {...field} />
                          </FormControl>
                          <FormDescription>
                            A descriptive name for this token
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description (Optional)</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Describe what this token will be used for..."
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="systemName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>External System Name (Optional)</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g., direct-garment-sales, b2b-repayments" {...field} />
                          </FormControl>
                          <FormDescription>
                            Identifies the source system on incoming payment requests. Used with external_reference for deduplication.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="autoApprove"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                          <div className="space-y-0.5">
                            <FormLabel>Auto-approve requests</FormLabel>
                            <FormDescription className="text-xs">
                              Requests from this system skip the approval chain and land as approved. Use only for trusted internal systems.
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="expiresInDays"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Expires In</FormLabel>
                          <Select onValueChange={(value) => field.onChange(Number(value))}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select expiration period" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="30">30 days</SelectItem>
                              <SelectItem value="90">90 days</SelectItem>
                              <SelectItem value="180">6 months</SelectItem>
                              <SelectItem value="365">1 year</SelectItem>
                              <SelectItem value="730">2 years</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex space-x-2">
                      <Button type="submit" disabled={loading}>
                        {loading ? 'Generating...' : 'Generate Token'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsCreateDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </Form>
              )}
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-4">
          {tokens.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No API tokens created yet. Generate your first token to get started.
            </div>
          ) : (
            tokens.map((token) => (
              <div key={token.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                    <h4 className="font-medium">{token.name}</h4>
                    <Badge variant={
                      !token.is_active ? 'destructive' :
                      isExpired(token.expires_at) ? 'secondary' :
                      'default'
                    }>
                      {!token.is_active ? 'Revoked' : isExpired(token.expires_at) ? 'Expired' : 'Active'}
                    </Badge>
                    {token.system_name && (
                      <Badge variant="outline">System: {token.system_name}</Badge>
                    )}
                    {token.auto_approve && (
                      <Badge variant="secondary">Auto-approve</Badge>
                    )}
                  </div>
                  {token.description && (
                    <p className="text-sm text-muted-foreground">{token.description}</p>
                  )}
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>Created: {formatDate(token.created_at)}</p>
                    <p>Expires: {formatDate(token.expires_at)}</p>
                    {token.last_used_at && (
                      <p>Last used: {formatDate(token.last_used_at)}</p>
                    )}
                  </div>
                </div>
                {token.is_active && !isExpired(token.expires_at) && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => revokeToken(token.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Revoke
                  </Button>
                )}
              </div>
            ))
          )}
        </div>

        <div className="bg-muted p-4 rounded-lg space-y-4">
          <div>
            <h4 className="font-medium mb-2">Purchase Data API</h4>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>Read purchase data from external systems:</p>
              <code className="block bg-background p-2 rounded border">
                POST /functions/v1/get-purchase-data<br />
                Authorization: Bearer YOUR_API_TOKEN
              </code>
            </div>
          </div>
          <div>
            <h4 className="font-medium mb-2">External Payment Requests API</h4>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>Submit payment requests directly from external systems (Direct Garment Sales, B2B Repayments, etc.):</p>
              <code className="block bg-background p-2 rounded border whitespace-pre overflow-x-auto text-xs">
{`POST /functions/v1/external-payment-requests
Authorization: Bearer YOUR_API_TOKEN
Content-Type: application/json

{
  "external_reference": "sales_order_12345",
  "title": "Payment for Order #12345",
  "description": "Direct Garment Sales settlement",
  "vendor_name": "ACME Textiles",
  "vendor_bank_name": "CBE",
  "vendor_bank_account": "1000123456789",
  "amount": 50000,
  "total_amount": 50000,
  "currency": "ETB",
  "payment_method": "bank_transfer",
  "business_unit": "chipchip",
  "items": [
    { "description": "Item A", "quantity": 10, "unit_price": 5000, "amount": 50000 }
  ],
  "documents": [
    { "file_name": "invoice.pdf", "file_type": "application/pdf", "base64": "JVBERi0..." }
  ]
}`}
              </code>
              <ul className="list-disc pl-5 text-xs space-y-1">
                <li><code>external_reference</code> ensures idempotency — retries return the existing request.</li>
                <li>If the token has <em>Auto-approve</em> enabled, requests bypass the approval chain.</li>
                <li>Documents are base64-encoded and stored under the request.</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>

    </Card>
  )
}